/**
 * Serialización del período entre las pantallas de dinero (Balance, Aportes, Movimientos).
 *
 * Existe porque el período se perdía en cada enlace nuevo que alguien agregaba. Se corrigió
 * cuatro veces seguidas —tarjeta de Aportes, tarjeta de Movimientos, pestañas de Aportes, y este
 * barrido— y cada corrección destapó otro camino que lo descartaba. El patrón de fondo era pedirle
 * a cada navegación que *se acordara* de pasar el período.
 *
 * La regla ahora es una: **toda navegación entre pantallas de dinero se construye con
 * `rutaConPeriodo()`**, que arrastra el período de la dirección actual sin que nadie tenga que
 * recordarlo. Si agregas un enlace entre estas pantallas y no usas este helper, lo estás
 * reintroduciendo.
 */

export interface Periodo {
  /** 1–12, o **0 para "todo el año"** (Balance y Movimientos lo soportan; Aportes no). */
  readonly mes: number;
  readonly año: number;
}

const PARAM_MES = 'mes';
const PARAM_ANIO = 'anio';

/**
 * Lee el período de la dirección. Devuelve `null` si no viene o es inválido, para que cada
 * pantalla aplique su propia regla por defecto.
 */
export function leerPeriodo(params: URLSearchParams): Periodo | null {
  const mes = Number(params.get(PARAM_MES));
  const año = Number(params.get(PARAM_ANIO));
  if (!Number.isInteger(año) || año <= 0) return null;
  if (!Number.isInteger(mes) || mes < 0 || mes > 12) return null;
  return { mes, año };
}

/** Serializa un período como cadena de consulta. Vacía si no hay período. */
export function queryDePeriodo(periodo: Periodo | null): string {
  if (!periodo) return '';
  return `?${PARAM_MES}=${periodo.mes}&${PARAM_ANIO}=${periodo.año}`;
}

/**
 * Construye una ruta arrastrando el período que traiga la dirección actual.
 *
 * Conserva el período **tal como llegó**, no el efectivo de la pantalla: si el usuario venía
 * mirando "todo el año" y Aportes cayó a su mes vigente porque no sabe mostrar períodos anuales,
 * volver debe devolverlo al año, no al mes al que Aportes cayó.
 */
export function rutaConPeriodo(ruta: string, search: string): string {
  const periodo = leerPeriodo(new URLSearchParams(search));
  const query = queryDePeriodo(periodo);
  if (!query) return ruta;
  // La ruta puede traer su propio parámetro (el panel de Administración pasa `volverA`),
  // así que el período se encadena en vez de abrir una consulta nueva.
  return ruta.includes('?') ? `${ruta}&${query.slice(1)}` : `${ruta}${query}`;
}
